import { initializeApp } from 'firebase/app';
import { getAuth as getFirebaseAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getAnalytics, isSupported } from 'firebase/analytics';

let firebaseInstance = null;

// Verificar si estamos en un entorno de desarrollo
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

// Firebase Hosting publica la config web del proyecto en /__/firebase/init.json, desde su
// CDN y en el mismo dominio: no tiene arranque en frío como la Cloud Function, que dejaba
// la pantalla en blanco uno o varios segundos antes de mostrar el login. En localhost esa
// ruta no existe y se usa la función.
const URL_CONFIG_HOSTING = '/__/firebase/init.json';
const URL_CONFIG_FUNCION = 'https://us-central1-transferisok.cloudfunctions.net/getFirebaseConfig-1';

const pedirConfig = async (url, signal) => {
  const response = await fetch(url, { signal, headers: { 'Cache-Control': 'no-cache' } });
  if (!response.ok) {
    throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

const obtenerConfig = async (signal) => {
  if (!isDevelopment) {
    try {
      const config = await pedirConfig(URL_CONFIG_HOSTING, signal);
      if (config && config.apiKey && config.projectId) return config;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.warn('Config de Hosting no disponible, se pide a la función:', error.message);
    }
  }
  return pedirConfig(URL_CONFIG_FUNCION, signal);
};

// index.js y otros módulos llaman a initializeFirebase a la vez al arrancar: comparten la
// misma promesa para pedir la config una sola vez.
let inicializando = null;

const initializeFirebase = () => {
  if (firebaseInstance) return Promise.resolve(firebaseInstance);
  if (!inicializando) {
    inicializando = inicializarConReintentos().finally(() => {
      inicializando = null;
    });
  }
  return inicializando;
};

const inicializarConReintentos = async (retryCount = 0) => {
  const maxRetries = 3;
  const retryDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s

  try {
    console.log(`🔥 Inicializando Firebase (intento ${retryCount + 1}/${maxRetries + 1})...`);

    // Crear AbortController para timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos timeout

    const firebaseConfig = await obtenerConfig(controller.signal);

    clearTimeout(timeoutId);
    console.log('✅ Configuración de Firebase obtenida correctamente');

    // Inicializar Firebase
    const app = initializeApp(firebaseConfig);
    const auth = getFirebaseAuth(app);
    const db = getFirestore(app);

    // Inicializar Analytics solo si es compatible y no estamos en desarrollo
    let analytics = null;
    if (!isDevelopment) {
      try {
        const isAnalyticsSupported = await isSupported();
        if (isAnalyticsSupported) {
          analytics = getAnalytics(app);
          console.log('📊 Firebase Analytics inicializado correctamente');

          // Configurar Analytics para mejor tracking
          analytics.config = {
            send_page_view: false, // Lo haremos manualmente
            allow_google_signals: true,
            allow_ad_personalization_signals: true
          };
        } else {
          console.log('📊 Analytics no es compatible en este entorno');
        }
      } catch (analyticsError) {
        console.error('📊 Error al inicializar Analytics:', analyticsError);
        analytics = null; // Asegurar que sea null si falla
      }
    } else {
      console.log('📊 Analytics desactivado en entorno de desarrollo');
    }

    firebaseInstance = { app, auth, db, analytics };
    console.log('🎉 Firebase inicializado correctamente');

    return firebaseInstance;
  } catch (error) {
    const isNetworkError = error.name === 'TypeError' && error.message.includes('fetch');
    const isTimeoutError = error.name === 'AbortError';
    const isConnectivityError = error.message.includes('Failed to fetch') ||
      isNetworkError || isTimeoutError;

    console.error(`🚨 Error inicializando Firebase (intento ${retryCount + 1}):`, {
      errorType: error.name,
      errorMessage: error.message,
      isNetworkError,
      isTimeoutError,
      isConnectivityError,
      retryCount
    });

    // Retry logic para errores de conectividad
    if (isConnectivityError && retryCount < maxRetries) {
      console.log(`🔄 Reintentando Firebase en ${retryDelay}ms... (${retryCount + 1}/${maxRetries})`);

      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return inicializarConReintentos(retryCount + 1);
    }

    // Si agotamos los retries, lanzar error
    console.error('❌ Máximo de reintentos de Firebase alcanzado');
    throw error;
  }
};

const getFirebaseInstance = () => {
  if (!firebaseInstance) {
    throw new Error('Firebase no está inicializado. Llama a initializeFirebase primero.');
  }
  return firebaseInstance;
};

const getAuthInstance = () => firebaseInstance?.auth;
const getDbInstance = () => firebaseInstance?.db;
const getAnalyticsInstance = () => firebaseInstance?.analytics;

// Inicializar Firebase inmediatamente al importar el módulo
initializeFirebase().catch(error => {
  console.error('Error durante la inicialización automática de Firebase:', error);
});

// Exportar todo junto al final
export {
  initializeFirebase,
  getFirebaseInstance,
  getAuthInstance,
  getDbInstance,
  getAnalyticsInstance
};